import React, { useState, useEffect } from 'react';
import { Button, Field, Input, Table, Modal, Message, Toast } from 'react-frontier';
import { Dropdown, Icon } from 'semantic-ui-react';
import { UserClass, ShipmentItemDetail } from '../SiteClasses';
import SearchUserModal from './SearchUserModal';
import classNames from 'classnames';
import Validator from '../Validator';
import API from '../API';

interface DeliverShipmentModalProps{
	open: boolean,
	onClose: ()=>void,
	shipment_id: number,
	items: ShipmentItemDetail[],
	onDelivered: (item_ids: number[])=>void,
}

enum ShownModal{
	SEARCH_USER = 1,
}

enum RecipientType{
	SYSTEM = 1,
	EXTERNAL = 2,
}

interface RecipientData{
	selectedUser: UserClass;
	recipientName: string;
	recipientContact: string;
}

var DeliverShipmentModal = (props: DeliverShipmentModalProps)=>{
	var [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.SYSTEM);
	var [recipient, setRecipient] = useState<RecipientData>({ selectedUser: null, recipientName: '', recipientContact: '' });
	var [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<ShownModal>(null);

	var activeItems = props.items ? props.items.filter(i => !i.delivered && !i.deleted) : [];

	useEffect(()=>{
		if(!props.open) return;
		setRecipientType(RecipientType.SYSTEM);
		setRecipient({ selectedUser: null, recipientName: '', recipientContact: '' });
		setSelectedItemIds([]);
		setPrompts(null);
		setLoading(false);
	}, [props.open]);

	var selectAll = ()=>{
		setSelectedItemIds(ids => ids.length === activeItems.length ? [] : activeItems.map(i => i.item_id));
	};

	var toggleItem = (item_id: number)=>{
		setSelectedItemIds(ids =>
			ids.includes(item_id) ? ids.filter(id => id !== item_id) : [...ids, item_id]
		);
	};

	var deliver = ()=>{
		var { valid, prompts } = Validator({
			user_recipient: recipient.selectedUser ? String(recipient.selectedUser.user_id) : '',
			recipient_name: recipient.recipientName.trim(),
			item_ids: selectedItemIds,
		}, {
			...(recipientType === RecipientType.SYSTEM ? {
				user_recipient: [{ rule: 'empty', prompt: 'Selecciona un usuario receptor.' }],
			} : {
				recipient_name: [{ rule: 'empty', prompt: 'Ingresa el nombre del receptor.' }],
			}),
			item_ids: [{
				rule: (v: number[]) => v && v.length > 0,
				prompt: 'Selecciona al menos un equipo a entregar.',
			}],
		});
		if(!valid) return setPrompts(prompts);

		setLoading(true);
		setPrompts(null);

		var data: { user_recipient?: number, recipient_name?: string, recipient_contact?: string } = {};
		if(recipientType === RecipientType.SYSTEM){
			data.user_recipient = recipient.selectedUser.user_id;
		} else {
			data.recipient_name = recipient.recipientName.trim();
			if(recipient.recipientContact.trim()) data.recipient_contact = recipient.recipientContact.trim();
		}

		API.deliverShipmentItems(props.shipment_id, selectedItemIds, data).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Equipos entregados correctamente');
			props.onDelivered(selectedItemIds);
			props.onClose();
		}).catch(()=>{
			setPrompts(['Hubo un error al registrar la entrega. (DSM-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	return <>
		<Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>Entregar equipos</Modal.Header>
			<Modal.Content>
				<Field label='Tipo de receptor'>
					<Dropdown
						fluid
						selection
						value={recipientType}
						options={[
							{ key: 'system', value: RecipientType.SYSTEM, text: 'Usuario del sistema' },
							{ key: 'external', value: RecipientType.EXTERNAL, text: 'Usuario externo' },
						]}
						onChange={(e, { value })=> setRecipientType(value as RecipientType)}
					/>
				</Field>
				{recipientType === RecipientType.SYSTEM ? (
					<Field>
						<Input
							value={recipient.selectedUser ? `${recipient.selectedUser.first_name} ${recipient.selectedUser.last_name}` : ''}
							readonly
							placeholder='Selecciona un usuario...'
							button={<Button text='Seleccionar' onClick={()=> setShownModal(ShownModal.SEARCH_USER)} />}
						/>
					</Field>
				) : <>
				<Field amount={2}>
					<Input
						label='Nombre'
						value={recipient.recipientName}
						onChange={(v: string)=> setRecipient(r => ({ ...r, recipientName: v }))}
					/>
					<Input
						label='Teléfono / Contacto'
						value={recipient.recipientContact}
						onChange={(v: string)=> setRecipient(r => ({ ...r, recipientContact: v }))}
					/>

				</Field>
				</>}
				<div style={{ textAlign: 'center', marginTop: 10 }}>
					<Button text='Seleccionar todos' color='black' onClick={selectAll} />
				</div>
				<Table
					title='Equipos'
					selectable
					fitted
					style={{ marginTop: 10 }}
					headers={[
						<div style={{ textAlign: 'center' }}>
							<i className="checkmark icon" style={{ marginRight: 0 }}></i>
						</div>,
						'ID', 'Tipo', 'Nombre', 'Serie'
					]}
					collapsingIndexes={[0, 1, 4]}
					onClick={(v: any[])=> {
						var itemId = v[1];
						toggleItem(itemId);
					}}
					data={activeItems.length === 0 ? [] : activeItems.map(item => {
						var selected = selectedItemIds.includes(item.item_id);
						return [
							<i className={classNames('large icon circle', {
								check: selected,
								outline: !selected
							})}></i>,
							item.item_id,
							item.type_name,
							item.item_name || <Icon name='minus' />,
							item.serial_number || <Icon name='minus' />,
						];
					})}
				/>
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={props.onClose} />
				<Button text='Entregar' color='black' loading={loading} onClick={deliver} />
			</Modal.Actions>
		</Modal>
		<SearchUserModal
			open={shownModal === ShownModal.SEARCH_USER}
			onClose={()=> setShownModal(null)}
			onSelected={(user)=>{ setRecipient(r => ({ ...r, selectedUser: user })); setShownModal(null); }}
		/>
	</>;
};

export default DeliverShipmentModal;
